/**
 * Where the open document goes when background work replaces the documents list (an export took the
 * open document out of it, a refresh, another window), and whether the person chose what opens instead.
 *
 * Tester report B-09 (6.2.46): an invoice the tester was checking exported on its own; the refresh that
 * took it out of the list opened Document 1, a W-9 still in Needs review, and the tester's next
 * Confirm & next confirmed the W-9 they had never looked at. What opens in place of a document that left
 * the list was not chosen, so the main button does not confirm it until the person has moved by
 * themselves or has been told (`mayConfirmShown`).
 */
export type SelectionAfterRefresh = {
  /** The position to open. */
  index: number;
  /** The id that opened without the person choosing it (their document left the list), or null. */
  movedUnder: string | null;
};

export function selectionAfterRefresh(
  shownId: string | undefined,
  documents: readonly { id: string }[],
  fallback: () => number,
): SelectionAfterRefresh {
  const kept = shownId ? documents.findIndex((item) => item.id === shownId) : -1;
  if (kept >= 0) return { index: kept, movedUnder: null };
  const index = fallback();
  return { index, movedUnder: shownId ? (documents[index]?.id ?? null) : null };
}

/**
 * The page to show after the refresh. This view's own saves come back as a refreshed list too (a
 * field save, a page load, Find the table, a boundary change, a background append), and each used
 * to send the open document back to page 1. The same document keeps its page (clamped, in case it
 * lost pages); a different document opens on its first.
 */
export function pageAfterRefresh(
  shownId: string | undefined,
  opened: { id: string; pages: readonly unknown[] } | undefined,
  currentPage: number,
): number {
  if (!shownId || !opened || opened.id !== shownId) return 0;
  return Math.max(0, Math.min(currentPage, opened.pages.length - 1));
}

/** Whether the main button may confirm the open document: it did not open under the person since they last moved. */
export const mayConfirmShown = (shownId: string, movedUnder: string | null): boolean =>
  shownId !== movedUnder;

/** What the bar says instead of confirming a document that opened under the person. */
export const MOVED_UNDER_NOTICE =
  'The document you were on left this list, so this is a different one. Check it, then confirm.';
